import i18n from '../i18n';
import Big from 'big.js';
import * as models from 'smg/admin/models';
import * as html from 'smg/html';
import Modal from 'smg/modal';
import { DollarFormat } from 'smg/util';
import { SourceOrder } from 'smg/cart-items';
import { EditorLines } from '../cart/pricebreakdown';

// One pending qty/unit-price edit, keyed in the module map by the quote/order
// (child) line item id it targets. `child` remembers which override ID field
// the server expects; `cartItemID` groups the edit under its summary row for
// the badge and for replacing a cart item's edits wholesale when its modal
// re-applies.
interface lineItemOverrideDraft {
    cartItemID: string;
    child: boolean;
    quantity?: number;
    price?: InstanceType<typeof Big>;
}

export interface LineItemEditorHandle {
    // Open the edit modal for a cart item (by its summary row). Renders from
    // the pristine page-load cart models plus any pending edits, so reopening
    // shows the edits without ever mistaking them for quoted values.
    open(cartItemID: string): void;
    // Whether the cart item has any pending edits — drives the summary row's
    // "Edited" badge.
    hasEdits(cartItemID: string): boolean;
    // Extra query params for the GET /carts/:id preview fetch (a
    // JSON-encoded array, since struct slices can't ride bare form params).
    // Empty object when there are no edits.
    params(): Record<string, string>;
    // The same edits as plain array values for the POST /v2/orders body —
    // the server applies them identically on both paths, so the totals the
    // preview showed are the totals the order persists.
    snapshot(): models.ICartLineItemOverride[];
}

export interface LineItemEditorDeps {
    // Fired after the admin applies changes in the modal — the summary
    // re-fetches the cart preview with the new edit params.
    onChange: () => void;
}

// SetupLineItemEditor owns the admin checkout's per-cart-item line item
// quantity/unit-price edits. Edits live only in this module's memory (and
// ride the preview fetch + order create) — nothing is written to the quote,
// order, or cart rows. The pristine cart from page load is kept as the
// comparison baseline so an edit set back to its original value drops out of
// the override list instead of riding along as a no-op.
export function SetupLineItemEditor(cartInit: models.ICartPreviewResponse | undefined, deps: LineItemEditorDeps): LineItemEditorHandle {
    // Pristine cart item models from page load, keyed by cart item id. The
    // preview fetch returns the cart with edits already applied, so display
    // re-renders use that — but the editor must always diff against these.
    const pristineCartItems = new Map<string, models.CartItem>();
    if (cartInit) {
        const cart = new models.CartPreviewResponse(cartInit);
        for (const cartItem of cart.cart.cartItems) {
            pristineCartItems.set(cartItem.id, cartItem);
        }
    }

    // Committed edits, keyed by the targeted line item id.
    const overrides = new Map<string, lineItemOverrideDraft>();

    function buildOverrides(): models.ICartLineItemOverride[] {
        const out: models.ICartLineItemOverride[] = [];
        for (const [id, draft] of overrides) {
            const override: models.ICartLineItemOverride = {};
            if (draft.child) {
                override.objectChildLineItemID = id;
            } else {
                override.objectLineItemID = id;
            }
            if (draft.quantity !== undefined) {
                override.quantity = draft.quantity;
            }
            if (draft.price !== undefined) {
                override.price = draft.price.toFixed(2);
            }
            out.push(override);
        }
        return out;
    }

    return {
        open: cartItemID => {
            const cartItem = pristineCartItems.get(cartItemID);
            if (!cartItem) {
                return;
            }
            openEditor(cartItem, overrides, deps.onChange);
        },
        hasEdits: cartItemID => {
            for (const draft of overrides.values()) {
                if (draft.cartItemID === cartItemID) {
                    return true;
                }
            }
            return false;
        },
        params: () => {
            const p: Record<string, string> = {};
            const lineItemOverrides = buildOverrides();
            if (lineItemOverrides.length > 0) {
                p['lineItemOverrides'] = JSON.stringify(lineItemOverrides);
            }
            return p;
        },
        snapshot: () => buildOverrides(),
    };
}

// One editable row in the modal — an existing quote/order (child) line item.
interface editorRow {
    id: string;
    child: boolean;
    // Child rows carry their parent's id plus the quantity-follow flags and
    // group key the modal needs to mirror the server's UpdateChildQuantities
    // on parent qty edits, so the modal total matches the preview.
    parentID?: string;
    linkedQuantity: boolean;
    autoQuantity: boolean;
    childGroup: string;
    pristineQuantity: number;
    pristinePrice: InstanceType<typeof Big>;
    $quantity: HTMLInputElement;
    $price: HTMLInputElement;
    $subtotal: HTMLElement;
}

function openEditor(
    cartItem: models.CartItem,
    overrides: Map<string, lineItemOverrideDraft>,
    onChange: () => void,
) {
    const quote = cartItem.quote;
    const order = SourceOrder(cartItem);
    // Same discriminator as the price-breakdown modal: a reorder cart item
    // carries an order but no quote serial.
    const isOrder = Boolean(order) && !quote?.serial;
    const label = isOrder ? `Order #${order?.invoiceNumber ?? ''}` : `#${quote?.serial ?? ''}`;

    const $body = html.FromString(`
        <p class="mb-3">
            <code class="fw-bold">${html.Escape(label)}</code>
            <span class="text-muted small ms-1">Changes apply to this order only — the original ${isOrder ? 'order' : 'quote'} isn't modified.</span>
        </p>
        <div class="table-responsive">
            <table class="table table-sm align-middle mb-0">
                <thead>
                    <tr>
                        <th class="text-uppercase text-muted small fw-normal border-top-0">Item</th>
                        <th class="text-uppercase text-muted small fw-normal text-end border-top-0">Qty</th>
                        <th class="text-uppercase text-muted small fw-normal text-end border-top-0">Unit price</th>
                        <th class="text-uppercase text-muted small fw-normal text-end border-top-0">Subtotal</th>
                    </tr>
                </thead>
                <tbody class="line-item-editor-rows"></tbody>
                <tfoot>
                    <tr>
                        <td colspan="3" class="text-end fw-bold border-top">Total</td>
                        <td class="text-end fw-bold border-top line-item-editor-total"></td>
                    </tr>
                </tfoot>
            </table>
        </div>
    `);

    const $rows = $body.querySelector('.line-item-editor-rows') as HTMLTableSectionElement;
    const $total = $body.querySelector('.line-item-editor-total') as HTMLElement;

    const rows: editorRow[] = [];

    // Mirror of the server's UpdateObjectChildQuantities for a parent qty
    // edit, so the modal's live total matches the preview the server computes
    // at Apply: linked children track the parent quantity outright; auto
    // children split what's left of the parent quantity after their group's
    // fixed (non-auto) children, remainder distributed one-at-a-time in row
    // order. Reserved sums use PRISTINE child quantities — the server runs
    // this recompute before child overrides apply, so modal edits to fixed
    // children don't shift the auto split there either.
    function followParentQuantity(parent: editorRow) {
        const parentQuantity = parseInt(parent.$quantity.value, 10);
        if (isNaN(parentQuantity)) {
            return;
        }
        const children = rows.filter(r => r.parentID === parent.id);
        const groups = new Map<string, { auto: editorRow[]; reserved: number }>();
        for (const child of children) {
            if (child.linkedQuantity) {
                child.$quantity.value = String(parentQuantity);
            }
            let group = groups.get(child.childGroup);
            if (!group) {
                group = { auto: [], reserved: 0 };
                groups.set(child.childGroup, group);
            }
            if (child.autoQuantity) {
                group.auto.push(child);
            } else {
                group.reserved += child.pristineQuantity;
            }
        }
        for (const group of groups.values()) {
            if (group.auto.length === 0) {
                continue;
            }
            const free = parentQuantity - group.reserved;
            if (free <= 0) {
                continue;
            }
            const base = Math.floor(free / group.auto.length);
            let remainder = free % group.auto.length;
            for (const child of group.auto) {
                if (remainder > 0) {
                    child.$quantity.value = String(base + 1);
                    remainder--;
                } else {
                    child.$quantity.value = String(base);
                }
            }
        }
    }

    function refreshTotal() {
        let total = Big(0);
        for (const row of rows) {
            const subtotal = rowSubtotal(row.$quantity, row.$price);
            row.$subtotal.textContent = DollarFormat(subtotal);
            total = total.add(subtotal);
        }
        $total.textContent = DollarFormat(total);
    }

    for (const line of EditorLines(cartItem)) {
        const override = overrides.get(line.id);
        const $row = html.FromString(`
            <tr>
                <td class="${line.child ? 'text-muted' : 'fw-bold'}" ${line.child ? 'style="padding-left:36px"' : ''}>${html.Escape(line.name)}</td>
                <td class="text-end">
                    <input type="number" class="form-control form-control-sm text-end d-inline-block line-item-editor-quantity" style="width:5.5rem" min="1" step="1" required>
                </td>
                <td class="text-end">
                    <span class="input-group input-group-sm flex-nowrap" style="width:7.5rem;margin-left:auto">
                        <span class="input-group-text">$</span>
                        <input type="number" class="form-control text-end line-item-editor-price" step="0.01" required>
                    </span>
                </td>
                <td class="text-end line-item-editor-subtotal text-nowrap"></td>
            </tr>
        `).firstElementChild as HTMLTableRowElement;

        const row: editorRow = {
            id: line.id,
            child: line.child,
            parentID: line.parentID,
            linkedQuantity: line.linkedQuantity ?? false,
            autoQuantity: line.autoQuantity ?? false,
            childGroup: line.childGroup ?? '',
            pristineQuantity: line.quantity,
            pristinePrice: line.price,
            $quantity: $row.querySelector('.line-item-editor-quantity') as HTMLInputElement,
            $price: $row.querySelector('.line-item-editor-price') as HTMLInputElement,
            $subtotal: $row.querySelector('.line-item-editor-subtotal') as HTMLElement,
        };
        row.$quantity.value = String(override?.quantity ?? line.quantity);
        row.$price.value = (override?.price ?? line.price).toFixed(2);
        row.$quantity.addEventListener('input', () => {
            // Parent qty edits drag linked/auto children along, mirroring the
            // server's recompute. The admin can still re-type a child qty
            // afterwards; their explicit override wins server-side too.
            if (!row.child) {
                followParentQuantity(row);
            }
            refreshTotal();
        });
        row.$price.addEventListener('input', refreshTotal);
        rows.push(row);
        $rows.appendChild($row);
    }
    refreshTotal();

    new Modal({
        title: i18n('editLineItems'),
        body: $body,
        size: 'modal-lg',
        confirmText: i18n('apply'),
        modalDialogScrollable: true,
        submitAutoFocus: false,
        onSubmit: () => {
            // Replace this cart item's committed edits wholesale: rows whose
            // values match the pristine quote/order line drop out entirely,
            // so clearing an edit really clears it.
            for (const [id, draft] of overrides) {
                if (draft.cartItemID === cartItem.id) {
                    overrides.delete(id);
                }
            }
            for (const row of rows) {
                const draft: lineItemOverrideDraft = { cartItemID: cartItem.id, child: row.child };
                const quantity = parseInt(row.$quantity.value, 10);
                if (quantity !== row.pristineQuantity) {
                    draft.quantity = quantity;
                }
                const price = Big(row.$price.value || 0);
                if (!price.eq(row.pristinePrice)) {
                    draft.price = price;
                }
                if (draft.quantity !== undefined || draft.price !== undefined) {
                    overrides.set(row.id, draft);
                }
            }

            onChange();
        },
    });
}

function rowSubtotal($quantity: HTMLInputElement, $price: HTMLInputElement): InstanceType<typeof Big> {
    const quantity = parseInt($quantity.value, 10);
    let price: InstanceType<typeof Big>;
    try {
        price = Big($price.value || 0);
    } catch {
        price = Big(0);
    }
    if (isNaN(quantity)) {
        return Big(0);
    }
    return price.mul(quantity);
}
